<template>
    <v-flex xs12 sm10 md8 lg6>
      <v-card class="px-2 py-4">
        <v-progress-linear :indeterminate="true" v-if="loading" class="loading"/>
        <v-flex xs12 px-3 pb-3 v-if="err">
          <v-alert type="error" :value="true">{{err}}</v-alert>
        </v-flex>
        <v-flex xs12 px-3 pb-3 v-if="success">
          <v-alert type="success" :value="true">{{success}}</v-alert>
        </v-flex>
        <v-form v-model="valid">
          <v-flex xs12 px-3>
          <v-autocomplete
            v-if="!isLocalAdmin"
            required
            :disabled="isEdit"
            :loading="companyLoading"
            :rules="[v => !!v || `${$lang.errors.missingCompany}`]"
            :items="companies"
            item-text="name"
            item-value="id"
            v-model="selectedCompany"
            :label="$lang.actions.selectCompany"
          />
          </v-flex>
          <v-flex xs12 px-3>
            <v-text-field
              v-model="data.ruleName"
              required
              :rules="[v => !!v || `${$lang.errors.missingRuleName}`]"
            >
              <template slot="label">
                {{ $lang.labels.ruleName }} - Visible to customer<span class="error--text"> *</span>
              </template>
            </v-text-field>
            <v-textarea
                auto-grow
                rows="1"
                v-model="description">
              <template slot="label">
                {{ $lang.labels.description }} - {{ $lang.labels.visibleToCustomer }}
              </template>
            </v-textarea>
            <h3>{{$lang.header.conditions}}</h3>
            <v-text-field
              :label="$lang.labels.amountOfPersons"
              v-model="amountOfPersons"
              type="number"
              min="0"
              max="50"
            />
            <v-autocomplete
              multiple
              :items="days"
              v-model="daysOfWeek"
              clearable>
              <template slot="label">
                {{ $lang.actions.selectDays }}<span class="error--text"> *</span>
              </template>
            </v-autocomplete>
            <v-autocomplete
              multiple
              :items="types"
              v-model="orderTypes"
              clearable>
              <template slot="label">
                {{ $lang.actions.selectOrderTypes }}<span class="error--text"> *</span>
              </template>
            </v-autocomplete>
            <v-autocomplete
              multiple
              :items="methods"
              v-model="paymentMethods"
              clearable>
              <template slot="label">
                {{ $lang.actions.selectPaymentMethods }}<span class="error--text"> *</span>
              </template>
            </v-autocomplete>
          </v-flex>
          <v-layout row wrap align-center justify-space-between>
            <v-flex xs12 sm6 px-3>
              <v-text-field
                v-model="startTime"
                type="text"
                mask="##:##"
                placeholder="HH:mm"
                :rules="timeRule">
                <template slot="label">
                  {{ $lang.labels.startTime }}<span class="error--text"> *</span>
                </template>
              </v-text-field>
            </v-flex>
            <v-flex xs12 sm6 px-3>
              <v-text-field
                type="text"
                v-model="endTime"
                mask="##:##"
                placeholder="HH:mm"
                :rules="timeRule">
                <template slot="label">
                  {{ $lang.labels.endTime }}<span class="error--text"> *</span>
                </template>
              </v-text-field>
            </v-flex>
          </v-layout>
          <v-flex xs12 px-3>
            <v-text-field
              :label="$lang.labels.minAmount"
              v-model="minAmount"
              type="number"
              max="100"
              min="0"
            />
            <v-text-field
              :label="$lang.labels.voucherCode"
              v-model="voucherCode"
            />
          </v-flex>
          <v-flex xs12 px-3>
            <h3>{{$lang.header.values}}</h3>
            <v-text-field
              :label="$lang.labels.discountAmount"
              v-model="discountAmount"
              type="number"
              max="100"
              min="0"
              clearable
              :disabled="discountPercent !== null"
            />
            <v-text-field
              :label="$lang.labels.discountPercent"
              v-model="discountPercent"
              type="number"
              max="100"
              min="0"
              suffix="%"
              :disabled="discountAmount !== null"
              clearable
            />
          </v-flex>
        </v-form>
        <v-layout row wrap justify-space-between class>
          <v-text-field
            v-if="isEdit && createdOn"
            :label="$lang.labels.created"
            v-model="createdOn"
            readonly
            class="min-w max-w mx-3"
          />
          <v-text-field
            v-if="isEdit && modifiedOn"
            :label="$lang.labels.modified"
            v-model="modifiedOn"
            readonly
            class="min-w max-w mx-3"
          />
        </v-layout>
        <v-layout row justify-space-between>
          <v-btn flat class="ml-0" @click="$router.push({ name: 'discountRules' })"><v-icon>keyboard_arrow_left</v-icon> Close</v-btn>
          <v-btn class="primary" :disabled="!validation" @click="submit()">{{ isEdit ? $lang.actions.update : $lang.actions.create}}</v-btn>
        </v-layout>
      </v-card>
    </v-flex>
</template>

<script>
  import {DateTime} from 'luxon';

  export default {
    data() {
      return{
        isLocalAdmin: true,
        companyLoading: true,
        search: '',
        companies: [],
        selectedCompany: null,
        err:'',
        isEdit: false,
        success: '',
        valid: false,
        loading: false,
        days: [
          {text: this.$lang.labels.sunday, value:'Sunday'},
          {text: this.$lang.labels.monday, value:'Monday'},
          {text: this.$lang.labels.tuesday, value:'Tuesday'},
          {text: this.$lang.labels.wednesday, value:'Wednesday'},
          {text: this.$lang.labels.thursday, value:'Thursday'},
          {text: this.$lang.labels.friday, value:'Friday'},
          {text: this.$lang.labels.saturday, value:'Saturday'},
        ],
        types: ['collection','delivery'],
        methods: ['cash','card'],
        discountAmount: null,
        discountPercent: null,
        amountOfPersons: null,
        daysOfWeek: [],
        orderTypes: [],
        paymentMethods: [],
        startTime: null,
        endTime: null,
        minAmount: null,
        voucherCode: null,
        description: null,
        data: {
          companyId: `${this.$route.params.companyId}`,
          ruleName: null,
          conditions: {},
          value: {},
        },
        timeRule: [
          v => /^([01]\d|2[0-3]):?([0-5]\d)$/.test(v) || this.$lang.errors.validTime
        ],
        createdOn: '',
        modifiedOn: '',
      };
    },
    computed: {
      validation() {
        let startTime = this.startTime;
        let endTime = this.endTime;
        let valid = this.valid;
        let isValidStart = true;
        let isValidEnd = true;
        if (startTime !== null) {
          isValidStart = this.validateTime(startTime);
        }
        if (endTime !== null) {
          isValidEnd = this.validateTime(endTime);
        }
        if (isValidStart && isValidEnd && valid) {
          return true;
        }
        return false;
      },
    },
    watch: {
      selectedCompany(val) {
        if (val) {
          this.data.companyId = val;
        }
      },
      err(val) {
        if (val !== '') {
          setTimeout(() => { this.err = ''; }, 5000);
        }
      },
    },
    methods: {
      formatDate(date) {
        return DateTime.fromISO(date).toFormat('yyyy-MM-dd HH:mm:ss');
      },
      validateTime(time) {
        if (!time) return false;
        let valid = true;
        if (
          parseInt(time[0], 10) > 2 ||
          parseInt(time[0], 10) === 2 && parseInt(time[1], 10) > 3 ||
          parseInt(time[2], 10) > 5 ||
          !time[3]
        ) valid = false;
        return valid;
      },
      formatTime(time) {
        return [time.slice(0, 2), ':', time.slice(2)].join('');
      },
      submit() {
        const data = Object.assign({}, this.data);
        if (this.amountOfPersons !== null) {
          Object.assign(data.conditions, { amountOfPersons: this.amountOfPersons });
        }
        if (this.daysOfWeek.length !== 0) {
          Object.assign(data.conditions, { daysOfWeek: this.daysOfWeek });
        }
        if (this.orderTypes.length !== 0) {
          Object.assign(data.conditions, { orderTypes: this.orderTypes });
        }
        if (this.paymentMethods.length !== 0) {
          Object.assign(data.conditions, { paymentMethods: this.paymentMethods });
        }
        if (this.startTime !== null) {
          Object.assign(data.conditions, { startTime: this.startTime });
        }
        if (this.endTime !== null) {
          Object.assign(data.conditions, { endTime: this.endTime });
        }
        if (this.minAmount !== null) {
          Object.assign(data.conditions, { minAmount: this.minAmount });
        }
        Object.assign(data.conditions, { voucherCode: !this.voucherCode ? null : this.voucherCode });

        if (Object.keys(data.conditions).length === 0) {
          this.err = 'You must add at least one condition';
          return;
        }
        if (this.discountAmount !== null) {
          Object.assign(data, { value: { discountAmount: this.discountAmount }})
        }
        if (this.discountPercent !== null) {
          Object.assign(data, { value: { discountPercent: this.discountPercent }})
        }
        if (Object.keys(data.value).length === 0) {
          this.err = 'You must add value';
          return;
        }
        if (data.conditions.startTime) {
          data.conditions.startTime = this.formatTime(data.conditions.startTime);
        }
        if (data.conditions.endTime) {
          data.conditions.endTime = this.formatTime(data.conditions.endTime);
        }
        if (this.description !== null) {
          Object.assign(data, { description: this.description });
        }
        this.loading = true;
        let url = this.isEdit ? `discountrule/update/${this.$route.params.id}` : 'discountrule/create';
        this.$http.post(url, data)
          .then((res) => {
            if (res.body.status === 'Success') {
              this.loading = false;
              this.success = this.isEdit ? 'Discount Rule updated successfuly' : 'Discount Rule created successfuly';
              setTimeout(() => {
                if (this.isEdit) {
                  this.success = '';
                } else {
                  this.$router.push({ name: 'discountRules' });
                }
              }, 3000);
              return;
            }
            this.err = res.body.statusText;
          }, (err) => {
            this.loading = false;
            this.err = err.body.statusText || err.body.message;
          });
      },
      init() {
        return new Promise((resolve) => {
          this.$http.get('company')
            .then((res) => {
              resolve()
              this.companyLoading = false;
              if (res.body.status === 'Success') {
                this.companies = res.body.companies;
                this.selectedCompany = this.$route.params.id ? res.body.companies[0].id : this.$route.params.companyId;
                return;
              }
              this.err = res.body.statusText;
            }, (err) => {
              resolve()
              this.companyLoading = false;
              this.err = err.body.statusText || err.body.message;
            });
        });
      },
    },
    created() {
      this.isLocalAdmin = this.$auth.roles && this.$auth.roles.includes('ROLE_SUN_LOCAL_ADMIN');
      this.init()
        .then(() => {
          if (this.$route.params.id) {
            this.isEdit = true;
            this.$http.get(`discountrule/${this.$route.params.id}`)
              .then((res) => {
                if (res.body.status === 'Success') {
                  const data = res.body.discountRule;
                  if (data.value.discountAmount) {
                    this.discountAmount = data.value.discountAmount;
                  }
                  if (data.value.discountPercent) {
                    this.discountPercent = data.value.discountPercent;
                  }
                  if (data.conditions.amountOfPersons) {
                    this.amountOfPersons = data.conditions.amountOfPersons;
                  }
                  if (data.conditions.daysOfWeek) {
                    this.daysOfWeek = data.conditions.daysOfWeek;
                  }
                  if (data.conditions.orderTypes) {
                    this.orderTypes = data.conditions.orderTypes;
                  }
                  if (data.conditions.paymentMethods) {
                    this.paymentMethods = data.conditions.paymentMethods;
                  }
                  if (data.conditions.startTime) {
                    this.startTime = data.conditions.startTime;
                  }
                  if (data.conditions.endTime) {
                    this.endTime = data.conditions.endTime;
                  }
                  if (data.conditions.minAmount) {
                    this.minAmount = data.conditions.minAmount;
                  }
                  if (data.conditions.voucherCode) {
                    this.voucherCode = data.conditions.voucherCode;
                  }
                  if (data.description) {
                    this.description = data.description;
                  }
                  this.createdOn = this.formatDate(data.createdOn);
                  if (data.modifiedOn) {
                    this.modifiedOn = this.formatDate(data.modifiedOn);
                  }
                  this.data.ruleName = data.ruleName;
                  this.selectedCompany = data.companyId;
                }
              }, (err) => {
                this.err = err.body.statusText || err.body.message;
              })
          }
        });
    },
  };
</script>

<style lang="scss" scoped>
  .loading {
    position: absolute;
    top: 0;
    margin: 0;
    left: 0;
  }
</style>



// WEBPACK FOOTER //
// DiscountRuleDetails.vue?e6725ed6